import React, { useState, useRef, useEffect } from 'react';
import { useApiCall } from "../context/SnpApiContext";
import '../css/SnpChatWidget.css';
import {
    CHAT_IMAGE_URL,
    DESTINATION,
    Direction,
    HTTP_SUCCESS,
    MessageType,
    PayLoadType,
    SOURCE,
    ToneType,
    UsesType,
    WhichMessage
} from '../utils/SnpChatConstant';
import {
    getHighPrecisionTimestamp,
    isEmptyData,
    printChatDebugLog
} from '../utils/SnpChatUtils';

const SnpChatWidget = ({
    sessionId,
    mUserDetails,
    viaAdmin,
    registerSocketHandler,
    unregisterSocketHandler,
    setChatAdminHead,
    showImageView,
    initiateSoundNotification,
    setRefreshChat,
    whoStopped,
    setWhoStopped
}) => {

    const TOGGLE_WIDTH = 160;
    const TOGGLE_HEIGHT = 50;
    const EDGE_PADDING = 20;

    const getInitialPosition = () => ({
        x: window.innerWidth - TOGGLE_WIDTH,
        y: window.innerHeight - TOGGLE_HEIGHT - EDGE_PADDING,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    const [uploadingImages, setUploadingImages] = useState({});
    const [input, setInput] = useState('');
    const chatEndRef = useRef(null);
    const [chatSessionId, setChatSessionId] = useState('');
    const [userDetails, setUserDetails] = useState({ id: "", name: '', email: '', query: '' });
    const [chatClosed, setChatClosed] = useState(false);
    const [customerUpdateForm, setCustomerUpdateForm] = useState({ show: false, message: "" });
    const [imageFile, setImageFile] = useState('');
    const [loader, setLoader] = useState({ show: false, message: "" });
    const [noOneOnline, setNoOneOnline] = useState(false);
    const [admin, setAdmin] = useState(false);
    const [customerDetails, setCustomerDetails] = useState({
        endRequest: false,
        msg: "",
        name: "",
        email: "",
        show: false
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [showChatToast, setShowChatToast] = useState('');

    const [position, setPosition] = useState(getInitialPosition);

    const [messages, setMessages] = useState([]);
    const messagesRef = useRef(messages);
    const sessionRef = useRef("");
    const adminRef = useRef(false);

    const dragRef = useRef({
        isDragging: false,
        hasMoved: false,
        startX: 0,
        startY: 0,
        offsetX: 0,
        offsetY: 0,
    });

    const {
        createChatSession,
        stopChatSession,
        uploadImage,
        connectWsSession,
        sendPayLoadMessage,
        stopWsSession,
        updateCustomerDetails,
        getLiveMessages,
        exportChatSession,
        getCustomeDetailsAvailable,
        requestCustomerUpdate
    } = useApiCall();

    const triggerLoader = (show, message = "") => {
        setLoader(prev => ({
            ...prev,
            show,
            message,
        }));
    };

    const triggerError = (message, delay = 4000) => {
        setErrorMessage(message);
        setShowChatToast("chat-error-banner");
        setTimeout(() => setShowChatToast(""), delay);
    };

    const triggerSuccess = (message, delay = 4000) => {
        setErrorMessage(message);
        setShowChatToast("chat-success-banner");
        setTimeout(() => setShowChatToast(""), delay);
    };

    const getMsgFrom = (msgFrom, direction) => {
        if (isEmptyData(msgFrom)) {
            return direction === Direction.RIGHT ? "You" : "Support";
        }
        return direction === Direction.RIGHT ? `You (${msgFrom})` : msgFrom;
    };

    const toggleMaximize = () => setIsMaximized(prev => !prev);

    const toggleOpen = (mOpen) => {
        setIsOpen(mOpen);
        setChatAdminHead(mOpen);
    };

    const handleChatMessage = (status, errorMessage, chatPayload) => {
        printChatDebugLog("ChatContext:handleChatMessage", status, errorMessage, chatPayload, messagesRef.current);
        const currentMessages = messagesRef.current;

        if (!currentMessages || currentMessages.length === 0) {
            console.warn("ChatContext:No messages to update.");
            return;
        }

        if (status === HTTP_SUCCESS) {
            if (
                chatPayload.whichMessage === WhichMessage.BROADCAST_NEW_CHAT_SESSION ||
                chatPayload.whichMessage === WhichMessage.MESSAGE_DATA
            ) {
                if (chatPayload.whichMessage === WhichMessage.BROADCAST_NEW_CHAT_SESSION && adminRef.current) {
                    setWhoStopped(0);
                }

                const direction = chatPayload.msgFromId === userDetails.id ? Direction.RIGHT : Direction.LEFT;
                const newMsg = {
                    id: chatPayload.id,
                    msgFrom: getMsgFrom(chatPayload.msgFrom, direction),
                    message: chatPayload.message,
                    msgAt: chatPayload.msgAt,
                    direction,
                    messageType: chatPayload.messageType,
                    msgFromId: chatPayload.msgFromId,
                    sessionId: chatPayload.sessionId,
                    local: false,
                    failed: false,
                };

                const newMessageList = [...currentMessages, newMsg];
                setMessages(newMessageList);
            } else if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_CUSTOMER_UPDATE) {
                if (adminRef.current) {
                    initiateSoundNotification(ToneType.New_Message_Receive);
                    triggerSuccess("Customer Updated their details", 3000);
                } else {
                    setCustomerUpdateForm(prev => ({
                        ...prev,
                        show: true,
                        message: "Admin requesting to update your details"
                    }));
                }
            } else if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_SUCCESS) {
                const updatedMessages = currentMessages.map(msg =>
                    msg.id === chatPayload.mapId
                        ? {
                            ...msg,
                            local: false,
                            failed: false,
                            id: chatPayload.id,
                            msgAt: chatPayload.msgAt,
                        }
                        : msg
                );
                setMessages(updatedMessages);
            } else if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_DESTINATION_JOINED) {
                initiateSoundNotification(ToneType.New_Message_Receive);
                triggerSuccess(`${chatPayload.destinationJoinedName}`, 4000);
            } else if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_CLOSED) {
                const rAdmin = chatPayload.admin || false;
                let rStopBy = chatPayload.stopBy;

                if (isEmptyData(rStopBy)) {
                    rStopBy = "Chat Session is Closed, clearing the details";
                } else {
                    rStopBy = `Chat Session is Closed,(${rStopBy}) clearing the details`;
                }

                triggerSuccess(rStopBy, 4000);
                initiateSoundNotification(ToneType.New_Message_Receive);
                clearChatSession();

                if (rAdmin) {
                    if (!adminRef.current) {
                        setWhoStopped(2);
                    }
                } else {
                    setRefreshChat(true);
                }
            }
        } else {
            triggerError(`Failed (Error Code : ${status} ${errorMessage} ). Please try again.`, 2500);

            if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_ERROR) {
                initiateSoundNotification(ToneType.Error);
                const updatedMessages = currentMessages.map(msg =>
                    msg.id === chatPayload.mapId
                        ? {
                            ...msg,
                            failed: true,
                            msgAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                        : msg
                );
                setMessages(updatedMessages);
            } else if (chatPayload.whichMessage === WhichMessage.BROADCAST_CHAT_CLOSED) {
                const rAdmin = chatPayload.admin || false;
                let rStopBy = chatPayload.stopBy;

                if (isEmptyData(rStopBy)) {
                    rStopBy = "Chat Session is Closed, clearing the details";
                } else {
                    rStopBy = `Chat Session is Closed,(${rStopBy}) clearing the details`;
                }

                triggerSuccess(rStopBy, 4000);
                initiateSoundNotification(ToneType.Error);
                setTimeout(() => clearChatSession(), 2000);

                if (rAdmin) {
                    if (!adminRef.current) {
                        setWhoStopped(2);
                    }
                } else {
                    setRefreshChat(true);
                }
            }
        }
    };

    const clearChatSession = () => {
        const localSessionId = sessionRef.current;
        if (isEmptyData(localSessionId)) {
            console.error("sessionRef is empty");
            return;
        }

        const localAdmin = adminRef.current;

        if (localAdmin) {
            handleExportChatSession(localSessionId);
            unregisterSocketHandler(localSessionId, true);
        } else {
            stopWsSession();
        }

        setWhoStopped(0);
        setMessages([]);
        setChatClosed(true);
        toggleOpen(false);
        setChatSessionId("");
        localStorage.removeItem('chatSessionId');
    };

    const handleExportChatSession = async (mSessionId) => {
        if (isEmptyData(mSessionId)) {
            console.warn("ChatContext:handleExportChatSession ", "Empty Session Id");
            return;
        }

        const data = await exportChatSession(mSessionId);
        if (data !== null) {
            printChatDebugLog("handleExportChatSession", "export for session " + mSessionId + " initiated");
        } else {
            console.warn("ChatContext:handleExportChatSession ", "invalid details " + sessionId);
        }
    };

    const handleSocketConnectError = (msg, isError, toneType) => {
        printChatDebugLog("handleSocketConnectError", msg, isError, toneType);
        if (!isError) {
            triggerSuccess(msg, 2500);
        } else {
            triggerError(msg, 3000);
        }
    };

    const handleConnectWsSession = (msgFromId, viaAdmin = false) => {
        printChatDebugLog("ChatContext:handleConnectWsSession", msgFromId, viaAdmin);

        if (viaAdmin) {
            registerSocketHandler(msgFromId, (status, errorMessage, chatPayload) => {
                printChatDebugLog("ChatContext:handleConnectWsSession admin", status, errorMessage, chatPayload);
                handleChatMessage(status, errorMessage, chatPayload);
            });
        } else {
            connectWsSession(
                msgFromId,
                false,
                handleSocketConnectError,
                (isOpen) => {
                    if (isOpen) {
                        triggerSuccess("Chat Session Established ... ", 2500);
                    } else {
                        triggerError("Chat Session Close/Disconnected", 2500);
                    }
                },
                (response, snpModel) => {
                    printChatDebugLog("ChatContext:handleConnectWsSession client", response, snpModel);
                    if (snpModel) {
                        const chatPayload = response.payLoad;
                        handleChatMessage(response.status, response.errorMessage, chatPayload);
                    } else {
                        console.log("ChatContext:connectWsSession in message format ", response);
                    }
                }
            );
        }
    };

    useEffect(() => {
        messagesRef.current = messages;
        printChatDebugLog("ChatContext:SnpChatWidget: ", messages);

        if (messages.length > 0) {
            requestAnimationFrame(() => {
                chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }, [messages]);

    const updateInternalMessageChat = (tempId, direction, payLoad) => {
        const newMsg = {
            id: payLoad.id,
            msgFrom: getMsgFrom(payLoad.msgFrom, direction),
            message: payLoad.message,
            msgAt: payLoad.msgAt,
            direction,
            messageType: payLoad.messageType,
            msgFromId: payLoad.msgFromId,
            sessionId: payLoad.sessionId,
            local: false,
            failed: false,
        };

        setMessages(prevMessages => {
            if (!isEmptyData(tempId)) {
                const index = prevMessages.findIndex(m => m.id === tempId);
                if (index !== -1) {
                    const updated = [...prevMessages];
                    updated[index] = { ...newMsg };
                    return updated;
                }
            }
            return [...prevMessages, newMsg];
        });

        setInput('');
    };

    const updateSendMessage = (tempId, payLoad) => {
        const currentMessages = messagesRef.current;
        const updatedMessages = currentMessages.map(msg =>
            msg.id === tempId
                ? {
                    ...msg,
                    message: payLoad.message,
                    local: false,
                    uploading: false,
                    id: payLoad.id,
                    msgFromId: payLoad.msgFromId,
                    msgAt: payLoad.msgAt,
                }
                : msg
        );
        setMessages(updatedMessages);
    };

    const handleEndChat = async () => {
        const data = await stopChatSession(admin, chatSessionId);
        if (data !== null) {
            printChatDebugLog("ChatContext:handleEndChat: ", data);
            if (data.status === HTTP_SUCCESS) {
                if (!isEmptyData(chatSessionId)) {
                    clearChatSession();
                } else {
                    console.warn("ChatContext:handleEndChat ", "chat session is already closed");
                }
            }
        } else {
            triggerError("Unable to end chat. Try again", 2500);
        }
        triggerLoader(false, "");
    };

    const handleEndOrDismisChat = () => {
        if (customerDetails.endRequest) {
            handleEndChat();
        } else {
            setCustomerDetails(prev => ({
                ...prev,
                endRequest: false,
                show: false,
                name: "",
                email: "",
                msg: ""
            }));
        }
    };

    const handleRequestCustomerUpdate = async () => {
        triggerLoader(true, "Initiating ... ");
        const data = await requestCustomerUpdate(chatSessionId);
        if (data !== null) {
            if (data === HTTP_SUCCESS) {
                triggerSuccess("Request to Customer Raise Successfully ", 3000);
            } else {
                triggerError("Failed to Request. Try again", 4000);
            }
        }
        triggerLoader(false);
        setCustomerDetails(prev => ({
            ...prev,
            endRequest: false,
            show: false,
            name: "",
            email: "",
            msg: ""
        }));
    };

    const handleGetCustDetails = async () => {
        const data = await getCustomeDetailsAvailable(chatSessionId);
        if (data !== null) {
            const payload = data.payLoad;
            if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_SUCCESS) {
                setCustomerDetails(prev => ({
                    ...prev,
                    show: true,
                    name: payload.sourceName,
                    email: payload.sourceEmail,
                    msg: "Customer Details: "
                }));
            } else if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_CUSTOMER_UPDATE) {
                triggerLoader(false, "");
                setCustomerDetails(prev => ({
                    ...prev,
                    endRequest: true,
                    show: true,
                    name: payload.sourceName,
                    email: "",
                    msg: "Customer Don't have Email , Do you End the chat"
                }));
            } else if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_CLOSED) {
                triggerLoader(false);
                triggerSuccess("Chat is already close clearing details", 3000);
                setTimeout(() => {
                    printChatDebugLog("Initiate Export Chat Session");
                    clearChatSession();
                }, 1000);
            }
        }
    };

    const handleCheckCustomerNEndChat = async () => {
        triggerLoader(true, "Initiate Stop Request ... ");
        if (admin) {
            const data = await getCustomeDetailsAvailable(chatSessionId);
            if (data !== null) {
                const payload = data.payLoad;
                if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_SUCCESS) {
                    handleEndChat();
                } else if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_CUSTOMER_UPDATE) {
                    triggerLoader(false, "");
                    setCustomerDetails(prev => ({
                        ...prev,
                        endRequest: true,
                        show: true,
                        name: payload.sourceName,
                        email: "",
                        msg: "Customer Don't have Email , Do you End the chat"
                    }));
                } else if (payload.whichMessage === WhichMessage.BROADCAST_CHAT_CLOSED) {
                    triggerLoader(false);
                    triggerSuccess("Chat is already close clearing details", 3000);
                    setTimeout(() => {
                        printChatDebugLog("Initiate Export Chat Session");
                        clearChatSession();
                    }, 1000);
                }
            }
        } else {
            handleEndChat();
        }
    };

    const handleUpdateCustomerDetails = async () => {
        if (noOneOnline) {
            if (isEmptyData(userDetails.email)) {
                triggerError("Unable to end chat. Please enter your email to end", 4000);
                return;
            }
        }

        const data = await updateCustomerDetails(userDetails.name, userDetails.email, chatSessionId);
        if (data !== null) {
            printChatDebugLog("ChatContext:handleUpdateCustomerDetails", data);
            if (data.status === HTTP_SUCCESS) {
                if (noOneOnline) {
                    localStorage.removeItem('chatSessionId');
                    setNoOneOnline(false);
                    setChatSessionId("");
                }
                triggerSuccess("Details updated successfully ... ", 2500);
                setCustomerUpdateForm(prev => ({ ...prev, show: false, message: "" }));
            } else {
                triggerError("Unable to update your details. Try again", 2500);
            }
        } else {
            triggerError("Unable to update your details. Try again", 2500);
        }
    };

    const handleStartChat = async () => {
        if (!isEmptyData(userDetails.query)) {
            triggerLoader(true, "Please wait, creating your session...");
            const data = await createChatSession({
                sourceId: userDetails.id,
                sourceName: userDetails.name,
                sourceEmail: userDetails.email,
                message: userDetails.query,
                messageType: MessageType.TYPE_TEXT
            });

            if (data !== null) {
                printChatDebugLog("ChatContext:handleStartChat: ", data);
                triggerLoader(false);

                if (data.status === HTTP_SUCCESS) {
                    const payLoad = data.payLoad;
                    setChatClosed(false);
                    setNoOneOnline(false);

                    if (payLoad.sendToNoClient) {
                        let errMsg = "Sorry for inconvenience, none of our support members are online. We've raised your query";
                        if (isEmptyData(userDetails.email)) {
                            errMsg = `${errMsg}, Please update your email.`;
                        } else {
                            errMsg = `${errMsg}, We will contact you on ${userDetails.email}`;
                        }

                        setCustomerUpdateForm(prev => ({
                            ...prev,
                            message: errMsg,
                        }));

                        setNoOneOnline(true);
                        setChatSessionId(payLoad.sessionId);
                    } else {
                        const newSessionId = payLoad.sessionId || "";
                        if (isEmptyData(newSessionId)) {
                            triggerError("Session ID is missing. Please try again.", 2500);
                            return;
                        }

                        const msgFromId = payLoad.msgFromId || "";
                        if (isEmptyData(msgFromId)) {
                            triggerError("Message From ID is missing. Please try again.", 2500);
                            return;
                        }

                        setChatSessionId(newSessionId);
                        localStorage.setItem("chatSessionId", newSessionId);
                        setUserDetails(prev => ({
                            ...prev,
                            id: msgFromId,
                        }));

                        updateInternalMessageChat("", Direction.RIGHT, payLoad);
                        handleConnectWsSession(msgFromId, false);
                    }
                } else {
                    triggerError(`Failed to create chat session(Error Code : ${data.status} ${data.errorMessage} ). Please try again.`, 2500);
                }
            } else {
                triggerLoader(false);
                triggerError("Failed to create chat session. Please try again.", 2500);
            }
        } else {
            triggerError("Please enter your query to start the chat.");
        }
    };

    const buildImageMessage = (file, tempId = Date.now()) => {
        const previewUrl = URL.createObjectURL(file);
        return {
            id: `id_${tempId}`,
            msgFrom: getMsgFrom(userDetails.name, Direction.RIGHT),
            message: previewUrl,
            msgAt: "",
            direction: Direction.RIGHT,
            messageType: MessageType.TYPE_IMAGE,
            local: true,
            sessionId: chatSessionId,
            uploading: true,
            imageUpload: true,
        };
    };

    const handleImageUpload = (file, tempId, tempMessage) => {
        uploadImage(
            file,
            chatSessionId,
            admin ? DESTINATION : SOURCE,
            tempId,
            (progress) => {
                setUploadingImages(prev => ({ ...prev, [tempId]: progress }));
            },
            (response) => {
                if (response.status === HTTP_SUCCESS) {
                    const payLoad = response.payLoad;
                    if (!isEmptyData(payLoad.insertFail)) {
                        BuildnSendChatInternalMessage(getHighPrecisionTimestamp(), payLoad.message, MessageType.TYPE_IMAGE);
                    } else {
                        if (!isEmptyData(payLoad.sendMessageFail)) {
                            BuildnSendChatInternalMessage(
                                payLoad.id,
                                payLoad.message,
                                MessageType.TYPE_IMAGE,
                                PayLoadType.SEND_MESSAGE_TO_OTHER
                            );
                        } else {
                            setUploadingImages(prev => {
                                const newMap = { ...prev };
                                delete newMap[tempId];
                                return newMap;
                            });
                            updateSendMessage(payLoad.mapId, payLoad);
                        }
                    }
                } else {
                    const currentMessages = messagesRef.current;
                    const updatedMessages = currentMessages.map(msg =>
                        msg.id === tempMessage.id
                            ? {
                                ...msg,
                                failed: true,
                                msgAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                                imageUpload: false,
                                uploading: false,
                            }
                            : msg
                    );
                    setMessages(updatedMessages);
                    triggerError(`Failed to send image(Error Code : ${response.status} ${response.errorMessage} ). Please try again.`, 4000);
                }
            }
        );
    };

    const handleMainImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const tempMessage = buildImageMessage(file);
            setMessages(prev => [...prev, tempMessage]);
            handleImageUpload(file, tempMessage.id, tempMessage);
        } else {
            triggerError("No Image attached. Please attach image & try again.");
        }
    };

    const BuildnSendChatInternalMessage = (
        id,
        msg,
        messageType,
        payLoadType = PayLoadType.AS_CHAT
    ) => {
        if (isEmptyData(id)) return;

        const newMsg = {
            id: `id_${id}`,
            msgFrom: getMsgFrom(userDetails.name, Direction.RIGHT),
            message: msg,
            msgFromId: userDetails.id,
            direction: Direction.RIGHT,
            messageType,
            local: true,
            sessionId: chatSessionId,
        };

        printChatDebugLog("BuildnSendChatInternalMessage", newMsg);
        setMessages(prev => [...prev, newMsg]);
        sendPayLoadMessage(newMsg, payLoadType);
    };

    const sendChatMessage = () => {
        if (isEmptyData(input)) {
            triggerError("Please enter your query to send a message.");
            return;
        }
        BuildnSendChatInternalMessage(getHighPrecisionTimestamp(), input, MessageType.TYPE_TEXT);
        setInput('');
    };

    const handleRetryMessage = (retryMsg) => {
        if (retryMsg.failed) {
            if (retryMsg.messageType === MessageType.TYPE_IMAGE && retryMsg.imageUpload) {
                handleImageUpload(imageFile, retryMsg.id, retryMsg);
            } else {
                const updatedMessages = messages.map(msg =>
                    msg.id === retryMsg.id
                        ? { ...msg, failed: false }
                        : msg
                );
                setMessages(updatedMessages);
                sendPayLoadMessage(retryMsg, PayLoadType.AS_CHAT);
            }
        }
    };

    const getMyLiveMessage = async (sessionId, msgFromId, page = 1, limit = 200) => {
        try {
            triggerLoader(true, "Loading ... ");
            const responsePayload = await getLiveMessages(sessionId, msgFromId, page, limit);
            if (responsePayload !== null && responsePayload.status === HTTP_SUCCESS) {
                setChatSessionId(sessionId);
                setMessages(responsePayload.payLoad.fulldata);
            }
            triggerLoader(false);
        } catch (err) {
            console.error("ChatContext:getMyLiveMessage", err);
            triggerLoader(false);
        }
    };

    const handlePointerDown = (e) => {
        e.preventDefault();

        dragRef.current.isDragging = true;
        dragRef.current.hasMoved = false;
        dragRef.current.startX = e.clientX;
        dragRef.current.startY = e.clientY;
        dragRef.current.offsetX = e.clientX - position.x;
        dragRef.current.offsetY = e.clientY - position.y;
        console.log("handlePointerDown", e.clientX, e.clientY, position);
        //setPosition(prev => ({ x: prev.x + 49, y: prev.y })); // Move the toggle button slightly to indicate it's active

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
    };

    const handlePointerMove = (e) => {
        if (!dragRef.current.isDragging) return;

        const dx = Math.abs(e.clientX - dragRef.current.startX);
        const dy = Math.abs(e.clientY - dragRef.current.startY);

        if (dx > 5 || dy > 5) {
            dragRef.current.hasMoved = true;
        }

        if (dragRef.current.hasMoved) {
            const nextX = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragRef.current.offsetX));
            const nextY = Math.max(0, Math.min(window.innerHeight - 60, e.clientY - dragRef.current.offsetY));
            console.log("handlePointerMove", nextX, nextY);
            setPosition({
                x: nextX,
                y: nextY,
            });
        }
    };

    const handlePointerUp = () => {
        if (!dragRef.current.hasMoved) {
            setIsOpen(prev => {
                const next = !prev;
                setChatAdminHead(next);
                if (next) {
                    setPosition(posPrev => ({
                        ...posPrev,
                        x: posPrev.x + 70,
                    }));
                } else {
                    setPosition(posPrev => ({
                        ...posPrev,
                        x: posPrev.x - 70,
                    }));
                }
                return next;
            });
        }

        dragRef.current.isDragging = false;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
    };

    useEffect(() => {
        if (!isEmptyData(sessionId)) {
            toggleOpen(true);
            setUserDetails(prev => ({
                ...prev,
                id: mUserDetails.id,
                name: mUserDetails.name,
                email: mUserDetails.email,
            }));
            getMyLiveMessage(sessionId, mUserDetails.id, 1, 100);
        }
    }, [sessionId]);

    useEffect(() => {
        setAdmin(viaAdmin);
        if (viaAdmin) {
            handleConnectWsSession(sessionId, true);
        }
    }, [viaAdmin]);

    useEffect(() => {
        printChatDebugLog("ChatContext:admin", admin);
        adminRef.current = admin;
    }, [admin]);

    useEffect(() => {
        printChatDebugLog("ChatContext:session", chatSessionId);
        sessionRef.current = chatSessionId;
    }, [chatSessionId]);

    useEffect(() => {
        printChatDebugLog("ChatContext:whoStopped", whoStopped);
        if (whoStopped > 0) {
            setChatClosed(true);
        }
    }, [whoStopped]);

    useEffect(() => {
        const handleResize = () => {
            setPosition(prev => ({
                x: Math.min(prev.x, window.innerWidth - TOGGLE_WIDTH - EDGE_PADDING),
                y: Math.min(prev.y, window.innerHeight - TOGGLE_HEIGHT - EDGE_PADDING),
            }));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const renderChatHeaderUi = () => {
        return (
            <div className="chat-header">
                <div className="chat-header-left">
                    <span className="chat-icon">💬</span>
                    <div className="chat-header-text">
                        <div className="title">Chat Support</div>
                        <div className="subtitle">How can I help</div>
                    </div>
                </div>

                <div className="chat-header-menu">
                    <span className="menu-icon">⋮</span>
                    <div className="menu-dropdown">
                        <div onClick={toggleMaximize}>
                            {isMaximized ? 'Minimize Widget' : 'Maximize Widget'}
                        </div>

                        {!admin && !isEmptyData(chatSessionId) && (
                            <div onClick={() => setCustomerUpdateForm(prev => ({ ...prev, show: true, message: "" }))}>
                                Update Your Details
                            </div>
                        )}

                        {admin && !isEmptyData(chatSessionId) && (
                            <div onClick={handleGetCustDetails}>Customer Details</div>
                        )}

                        {!isEmptyData(chatSessionId) && (
                            <div onClick={handleCheckCustomerNEndChat}>End Chat</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderChatOnboardUi = () => {
        return (
            <div className="chat-onboard p-6 max-w-md mx-auto bg-white shadow-xl rounded-2xl space-y-4 border border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-800">Start a Chat</h2>

                <input
                    type="text"
                    placeholder="Your Name"
                    value={userDetails.name}
                    onChange={e => setUserDetails(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-600"
                />

                <input
                    type="email"
                    placeholder="Your Email"
                    value={userDetails.email}
                    onChange={e => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-600"
                />

                <textarea
                    placeholder="What's your query?"
                    value={userDetails.query}
                    onChange={e => setUserDetails(prev => ({ ...prev, query: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-cyan-600"
                />

                <button
                    className="w-full py-2 bg-cyan-700 hover:bg-cyan-800 text-white font-semibold rounded-lg transition duration-300"
                    onClick={handleStartChat}
                >
                    Start Chat
                </button>
            </div>
        );
    };

    const renderRequestCustomerUi = () => {
        return (
            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '12px',
                    padding: '20px',
                    background: '#fdfdfd',
                    maxWidth: '400px',
                    margin: '16px auto',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }}
            >
                <p
                    style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        fontSize: '14px',
                        color: '#333',
                        marginBottom: '8px'
                    }}
                >
                    {isEmptyData(customerUpdateForm.message) ? "Update your details" : customerUpdateForm.message}
                </p>

                <input
                    placeholder="Your Name"
                    value={userDetails.name}
                    onChange={e => setUserDetails({ ...userDetails, name: e.target.value })}
                    style={{
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        fontSize: '14px'
                    }}
                />

                <input
                    placeholder="Your Email"
                    value={userDetails.email}
                    onChange={e => setUserDetails({ ...userDetails, email: e.target.value })}
                    style={{
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        fontSize: '14px'
                    }}
                />

                <button
                    onClick={handleUpdateCustomerDetails}
                    style={{
                        padding: '10px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#007bff',
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                    }}
                >
                    {noOneOnline ? "End Chat" : "Submit"}
                </button>

                {!noOneOnline && (
                    <button
                        onClick={() => {
                            setCustomerUpdateForm(prev => ({ ...prev, show: false, message: "" }));
                        }}
                        style={{
                            padding: '10px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: '#bf374a',
                            color: 'white',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                    >
                        Dismiss
                    </button>
                )}
            </div>
        );
    };

    const renderChatClosedUi = () => {
        return (
            <>
                {whoStopped === UsesType.User && (
                    <div className="chat-closed-label">User left the conversation</div>
                )}

                {whoStopped === UsesType.Admin && (
                    <div className="chat-closed-label">Admin left the conversation</div>
                )}

                {!adminRef.current && (
                    <button className="chat-btn" onClick={handleStartChat}>
                        Start New Chat
                    </button>
                )}
            </>
        );
    };

    const renderAdminStopChatCustomerUpdate = () => {
        return (
            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '12px',
                    padding: '2px',
                    background: '#f9f9f9',
                    maxWidth: '400px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    margin: '4px'
                }}
            >
                {!isEmptyData(customerDetails.msg) && (
                    <p
                        style={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                            fontSize: "14px",
                            marginBottom: '12px',
                            color: '#333'
                        }}
                    >
                        {customerDetails.msg}
                    </p>
                )}

                <div style={{ marginBottom: '8px', fontSize: '13px', color: '#555' }}>
                    <strong>Name:</strong> {customerDetails.name}
                </div>

                <div style={{ marginBottom: '16px', fontSize: '13px', color: '#555' }}>
                    <strong>Email:</strong> {customerDetails.email}
                </div>

                <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', flexDirection: "column" }}>
                    <button className="chat-btn" onClick={handleRequestCustomerUpdate}>
                        Request Customer Detail
                    </button>

                    <button className="chat-btn-other" onClick={handleEndOrDismisChat}>
                        {customerDetails.endRequest ? "End Chat" : "Dismiss"}
                    </button>
                </div>
            </div>
        );
    };

    const renderChatInputUi = () => {
        return (
            <div className="chat-input">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type a message..."
                    onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()}
                />

                <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageUpload}
                    style={{ display: 'none' }}
                    id="imgUpload"
                />

                <label htmlFor="imgUpload" className="image-upload-icon">📷</label>
                <button onClick={sendChatMessage}>Send</button>
            </div>
        );
    };

    const renderChatBodyUi = () => {
        return (
            <>
                <div className="chat-body">
                    {messages.map((msg, i) => (
                        <div key={i} className={`bubble ${msg.direction}`}>
                            <div className="bubble-name">{msg.msgFrom}</div>

                            {msg.messageType === MessageType.TYPE_TEXT ? (
                                <div className="bubble-text">{msg.message}</div>
                            ) : (
                                <div className="bubble-img-wrapper">
                                    <img
                                        src={msg.uploading ? msg.message : `${CHAT_IMAGE_URL}${msg.message}`}
                                        alt="media"
                                        onClick={() => {
                                            showImageView(prev => ({ ...prev, show: true, url: msg.message }));
                                            toggleOpen(false);
                                        }}
                                        className={`bubble-img ${msg.uploading ? 'blurred' : ''}`}
                                    />

                                    {msg.uploading && (
                                        <div className="upload-progress">
                                            {uploadingImages[msg.id] || 0}%
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bubble-time" onClick={() => handleRetryMessage(msg)}>
                                {msg.msgAt}

                                {msg.local && !msg.failed && (
                                    <span className="bubble-tick">✔</span>
                                )}

                                {msg.failed && (
                                    <div className="bubble-failed" onClick={() => handleRetryMessage(msg)}>
                                        <span className="retry-icon" title="Tap to resend">🔄</span>
                                        <span>Unable to send. Click to retry.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <div ref={chatEndRef}></div>
                </div>

                {chatClosed ? renderChatClosedUi() : renderChatInputUi()}
            </>
        );
    };

    const renderChatBoxUi = () => {
        return (
            <>
                {isOpen && (
                    <div className={`chat-box ${isMaximized ? 'maximized' : ''}`}>
                        {renderChatHeaderUi()}

                        {!isEmptyData(showChatToast) && (
                            <div className={showChatToast}>{errorMessage}</div>
                        )}

                        {loader.show ? (
                            <div className="chat-loader-wrapper">
                                <div className="chat-loader-spinner"></div>
                                <p className="chat-loader-text">
                                    {loader.message || "Please wait, loading your session..."}
                                </p>
                            </div>
                        ) : noOneOnline ? (
                            renderRequestCustomerUi()
                        ) : customerDetails.show ? (
                            renderAdminStopChatCustomerUpdate()
                        ) : (
                            isEmptyData(chatSessionId)
                                ? renderChatOnboardUi()
                                : customerUpdateForm.show
                                    ? renderRequestCustomerUi()
                                    : renderChatBodyUi()
                        )}
                    </div>
                )}
            </>
        );
    };

    return (
        <div
            className="chat-widget-wrapper"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
            }}
        >
            {renderChatBoxUi()}

            <button
                className={`chat-toggle ${isOpen ? 'open' : ''}`}
                onPointerDown={handlePointerDown}
                style={{ touchAction: "none" }}
            >
                {isOpen ? '−' : '💬 Chat Support'}
            </button>
        </div>
    );
};

export default SnpChatWidget;